import { useCallback, useEffect, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ProjectHeroCropDialogProps {
  open: boolean
  imageSrc: string | null
  onCancel: () => void
  onConfirm: (blob: Blob) => void
  pending?: boolean
}

/**
 * Phase 8 — 16:9 aspect-locked crop dialog for the Project Hero. Mirrors AvatarCropDialog with the
 * different aspect ratio + a square (not round) crop overlay.
 */
export const ProjectHeroCropDialog = ({
  open,
  imageSrc,
  onCancel,
  onConfirm,
  pending,
}: ProjectHeroCropDialogProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  useEffect(() => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
  }, [imageSrc])

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return
    const blob = await renderCrop(imageSrc, croppedAreaPixels)
    if (blob) onConfirm(blob)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Crop your project hero</DialogTitle>
          <DialogDescription>
            Drag and zoom to frame. 16:9 aspect ratio; the server resizes to the canonical sizes.
          </DialogDescription>
        </DialogHeader>

        {imageSrc && (
          <div className="relative h-72 w-full overflow-hidden rounded-md bg-muted">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={16 / 9}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={false}
            />
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="hero-crop-zoom" className="text-xs font-medium text-muted-foreground">
            Zoom
          </label>
          <input
            id="hero-crop-zoom"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={pending || !croppedAreaPixels}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const renderCrop = async (imageSrc: string, area: Area): Promise<Blob | null> => {
  const image = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = area.width
  canvas.height = area.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height)
  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'))
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', (e) => reject(e))
    img.src = src
  })
